<template>
  <Breadcrumb :items="breadcrumbs" />
  <div class="form-page">
    <div class="form-page-header">
      <div class="form-page-header__icon"><i class="pi pi-user-plus"></i></div>
      <div class="form-page-header__text">
        <h1 class="form-page-header__title">إضافة حجز {{ grade?.name ?? '' }}</h1>
        <p class="form-page-header__subtitle">تسجيل بيانات طالب جديد في الفصل الدراسي</p>
      </div>
    </div>

    <div class="container-fluid">
      <form @submit.prevent="sendForm">

        <div class="form-card">
          <div class="form-card__header">
            <div class="form-card__header-icon"><i class="pi pi-id-card"></i></div>
            <span class="form-card__title">البيانات الشخصية</span>
          </div>
          <div class="form-card__body">
            <div class="row g-4">

              <div class="col-lg-4 col-md-6 col-12">
                <div class="form-field">
                  <label class="field-label">اسم الطالب <span class="required">*</span></label>
                  <InputText v-model="student_name" placeholder="أدخل الاسم كاملاً" class="w-100" />
                  <span class="field-error" v-if="v$.student_name.$error">
                    <i class="pi pi-exclamation-circle"></i> برجاء ادخال اسم الطالب
                  </span>
                </div>
              </div>

              <div class="col-lg-4 col-md-6 col-12">
                <div class="form-field">
                  <label class="field-label">رقم هاتف الطالب <span class="required">*</span></label>
                  <InputText v-model="student_phone" inputmode="tel" placeholder="01XXXXXXXXX" dir="ltr" class="w-100" @keypress="onlyNumbers" />
                  <span class="field-error" v-if="v$.student_phone.$error">
                    <i class="pi pi-exclamation-circle"></i> برجاء ادخال رقم هاتف الطالب
                  </span>
                </div>
              </div>

              <div class="col-lg-4 col-md-6 col-12">
                <div class="form-field">
                  <label class="field-label">رقم هاتف ولي الأمر <span class="optional">اختياري</span></label>
                  <InputText v-model="parent_phone" inputmode="tel" placeholder="01XXXXXXXXX" dir="ltr" class="w-100" @keypress="onlyNumbers" />
                </div>
              </div>

              <div class="col-lg-4 col-md-6 col-12">
                <div class="form-field">
                  <label class="field-label">الجنس <span class="required">*</span></label>
                  <Dropdown v-model="selected_gender" :options="genders" showClear optionLabel="name" placeholder="اختر الجنس" class="w-100" />
                  <span class="field-error" v-if="v$.selected_gender.$error">
                    <i class="pi pi-exclamation-circle"></i> برجاء اختيار الجنس
                  </span>
                </div>
              </div>

              <div class="col-lg-4 col-md-6 col-12">
                <div class="form-field">
                  <label class="field-label">نظام التعليم <span class="optional">اختياري</span></label>
                  <Dropdown v-model="selected_type" :options="types" showClear optionLabel="name" placeholder="اختر نظام التعليم" class="w-100" />
                </div>
              </div>

              <div class="col-lg-4 col-md-6 col-12">
                <div class="form-field">
                  <label class="field-label">العنوان <span class="optional">اختياري</span></label>
                  <InputText v-model="address" placeholder="المدينة، الحي..." class="w-100" />
                </div>
              </div>

              <div class="col-lg-4 col-md-6 col-12">
                <div class="form-field">
                  <label class="field-label">درجة اللغة العربية <span class="optional">اختياري</span></label>
                  <InputText v-model="math_degree" inputmode="numeric" placeholder="الدرجة" class="w-100" @keypress="onlyNumbers" />
                </div>
              </div>

            </div>
          </div>
        </div>

        <div class="form-card">
          <div class="form-card__header">
            <div class="form-card__header-icon"><i class="pi pi-file-import"></i></div>
            <span class="form-card__title">صورة الشهادة</span>
          </div>
          <div class="form-card__body">
            <label class="field-label">رفع الشهادة <span class="optional">اختياري</span></label>
            <div :class="['upload-zone', { 'upload-zone--has-file': imageEntry }]">
              <div class="upload-zone__icon">
                <i :class="`pi ${imageEntry ? 'pi-check' : 'pi-cloud-upload'}`"></i>
              </div>
              <p class="upload-zone__label">{{ imageEntry ? fileData.name : 'اضغط لاختيار صورة الشهادة' }}</p>
              <p class="upload-zone__hint" v-if="!imageEntry">PNG, JPG, GIF</p>
              <input type="file" @change="onFileChange" accept="image/png,image/gif,image/jpeg,image/jpg" />
            </div>
          </div>
        </div>

        <div class="notice-box notice-box--warning">
          <i class="pi pi-whatsapp" style="font-size:1.25rem;"></i>
          <div>
            <strong>هل واجهتك مشكلة في الحجز؟</strong>
            <p class="mb-0 mt-1" style="font-size:0.875rem;">
              تواصل معنا على الواتساب:
              <a href="https://wa.me/+201111488849" target="_blank" style="font-weight:700; margin-inline:4px;">01111488849</a>
            </p>
          </div>
        </div>

        <div class="form-actions">
          <button type="submit" class="btn-submit" :disabled="formSending">
            <i class="pi pi-spin pi-spinner-dotted" v-if="formSending"></i>
            <i class="pi pi-check" v-else></i>
            {{ formSending ? 'جاري الإرسال...' : 'تأكيد الحجز' }}
          </button>
        </div>

      </form>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import InputText from 'primevue/inputtext';
import Swal from 'sweetalert2';
import useVuelidate from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import Dropdown from 'primevue/dropdown';
import Breadcrumb from '@/components/global/breadcrumb.vue';
import { useGradeStore } from '@/stores/useGradeStore.js';

export default {
  name: 'AddGradeStudent',
  components: { InputText, Dropdown, Breadcrumb },

  data() {
    return {
      v$: useVuelidate(),
      student_name: '',
      student_phone: null,
      parent_phone: null,
      address: '',
      math_degree: null,
      imageEntry: '',
      fileData: '',
      formSending: false,
      selected_gender: null,
      selected_type: null,
      genders: [
        { name: 'ولد', id: 1 },
        { name: 'بنت', id: 2 },
      ],
      types: [
        { name: 'عام', id: 1 },
        { name: 'أزهر', id: 2 },
      ],
    };
  },

  validations() {
    return {
      student_name: { required },
      student_phone: { required },
      selected_gender: { required },
    };
  },

  computed: {
    gradeStore() { return useGradeStore(); },
    gradeSlug() { return this.$route.params.gradeSlug; },
    grade() {
      return this.gradeStore.activeGrades.find(g => g.slug === this.gradeSlug)
          || this.gradeStore.allGrades.find(g => g.slug === this.gradeSlug)
          || null;
    },
    breadcrumbs() {
      return [
        { name: 'الرئيسية', path: '/' },
        { name: this.grade?.name ?? 'المرحلة', path: `/grades/${this.gradeSlug}/students` },
        { name: 'إضافة حجز', path: `/grades/${this.gradeSlug}/students/add` },
      ];
    },
  },

  methods: {
    onFileChange(e) {
      this.fileData = e.target.files[0];
      const reader = new FileReader();
      reader.readAsDataURL(this.fileData);
      reader.onload = () => (this.imageEntry = reader.result);
    },

    onlyNumbers(e) {
      const char = String.fromCharCode(e.which);
      if (!/[0-9٠-٩]/.test(char)) { e.preventDefault(); return; }
      const digits = (e.target.value.match(/[0-9٠-٩]/g) || []).length;
      if (digits >= 11) e.preventDefault();
    },

    toEnglishDigits(str) {
      return str ? str.replace(/[٠-٩]/g, d => '٠١٢٣٤٥٦٧٨٩'.indexOf(d)) : str;
    },

    async sendForm() {
      this.v$.$validate();
      if (this.v$.$error) return;

      const fd = new FormData();
      fd.append('student_name', this.student_name);
      fd.append('student_phone', this.toEnglishDigits(this.student_phone));
      if (this.parent_phone) fd.append('parent_phone', this.toEnglishDigits(this.parent_phone));
      if (this.address) fd.append('address', this.address);
      fd.append('gender', this.selected_gender.id);
      if (this.selected_type) fd.append('education_type', this.selected_type.id);
      if (this.math_degree) fd.append('math_degree', this.math_degree);

      if (this.grade?.id) fd.append('academic_year_id', this.grade.id);

      if (this.fileData instanceof File) fd.append('certificate_image', this.fileData);

      this.formSending = true;
      try {
        const response = await axios.post('/register_dashboard', fd);
        this.resetForm();
        Swal.fire({
          position: 'center',
          icon: 'success',
          title: 'رقم الهاتف: ' + response.data.data.phone,
          text: 'برجاء تذكر رقم الهاتف لمعرفة حالة الحجز',
        });
      } catch (err) {
        Swal.fire({ position: 'center', icon: 'error', title: err.response?.data?.message ?? 'حدث خطأ' });
      } finally {
        this.formSending = false;
      }
    },

    resetForm() {
      this.v$.$reset();
      this.student_name = '';
      this.student_phone = null;
      this.parent_phone = null;
      this.address = '';
      this.math_degree = null;
      this.imageEntry = '';
      this.fileData = '';
      this.selected_gender = null;
      this.selected_type = null;
    },
  },

  async mounted() {
    if (!this.gradeStore.activeGrades.length) await this.gradeStore.fetchActiveGrades();
    if (!this.gradeStore.allGrades.length) await this.gradeStore.fetchAllGrades();
  },
};
</script>
